import { fetchApi } from "@/lib/api";
import { useCurrentUser } from "@/lib/api/user";
import { useEffect, useRef } from "react";

/**
 * Hook that detects and updates user timezone automatically
 * - Only applies to mentors
 * - Respects user preferences (can be disabled via autoDetectTimezone setting)
 * - Implements a cooldown to avoid spamming the backend
 * - Only updates when timezone changes
 */
export function useTimezoneDetection() {
  const { data: user } = useCurrentUser();
  const lastUpdateRef = useRef<number>(0);
  const cooldownPeriod = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // Initialize the ref with value from localStorage if available
  useEffect(() => {
    const storedLastUpdate = localStorage.getItem("lastTimezoneUpdate");
    if (storedLastUpdate) {
      lastUpdateRef.current = parseInt(storedLastUpdate, 10);
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    // Only process if auto detection is enabled
    if (user.metadata?.auto_detect_timezone === false) {
      return;
    }

    const now = Date.now();
    // Check if we're within the cooldown period
    if (now - lastUpdateRef.current < cooldownPeriod) {
      return;
    }

    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const storedTimezone = user.metadata?.timezone;

    // Only update if the timezone has changed
    if (currentTimezone && currentTimezone !== storedTimezone) {
      console.log(
        `Updating timezone from ${storedTimezone} to ${currentTimezone}`,
      );

      fetchApi({
        url: "/v2/users/schedule",
        method: "PATCH",
        data: {
          timezone: currentTimezone,
          schedule: user.metadata?.schedule || [],
        },
      })
        .then(() => {
          console.log("Timezone updated successfully");
          lastUpdateRef.current = now;
          localStorage.setItem("lastTimezoneUpdate", now.toString());
        })
        .catch((error: Error) => {
          console.error("Failed to update timezone:", error);
        });
    } else {
      // Update the last update time even if we didn't change the timezone
      lastUpdateRef.current = now;
      localStorage.setItem("lastTimezoneUpdate", now.toString());
    }
  }, [user]);
}
